import { useCallback, useImperativeHandle, useState, forwardRef } from 'react';
import { Container, Row, Col, FormGroup, Input, Label } from 'reactstrap';
import { Modal, Button, Icon } from '@octano/global-ui';
import { useTexts } from '../hooks/useTexts';
import { DeleteEventScope } from '../types';

export type DeleteEventTarget = {
  reservationId: number;
  /** Cuántos días agenda ese mismo evento. 1 = reserva de un solo día. */
  daysInSeries: number;
};

interface DeleteEventModalProps {
  onConfirmDelete: (
    reservationId: number,
    scope: DeleteEventScope,
  ) => Promise<void>;
}

export interface DeleteEventModalMethods {
  open: (target: DeleteEventTarget) => void;
  close: () => void;
}

/**
 * Confirmación de borrado de un evento reservado del calendario de la sala.
 *
 * Si la reserva abarca varios días se pregunta el alcance, y se pregunta como
 * una ELECCIÓN —dos opciones y un único botón de confirmar— y no como tres
 * botones apilados: con tres, el destructivo compite con el de cancelar y
 * ninguno se lee como el camino por defecto.
 *
 * El pie de dos botones es el mismo de los modales de sala, así que el módulo
 * se comporta igual en todos lados. Por defecto viene marcado «sólo este día»,
 * que es la opción menos destructiva.
 *
 * El borrado es definitivo —a diferencia de la sala, que es soft delete— así
 * que el aviso lo dice explícitamente.
 */
const DeleteEventModal = forwardRef<
  DeleteEventModalMethods,
  DeleteEventModalProps
>(({ onConfirmDelete }, ref) => {
  const texts = useTexts();
  const [isOpen, setIsOpen] = useState(false);
  const [target, setTarget] = useState<DeleteEventTarget | null>(null);
  const [scope, setScope] = useState<DeleteEventScope>('day');
  const [isDeleting, setIsDeleting] = useState(false);

  const open = useCallback((next: DeleteEventTarget) => {
    setTarget(next);
    // Se reinicia al abrir y no al cerrar: el Modal anima la salida y cambiar
    // el estado ahí se ve durante el fade-out.
    setScope('day');
    setIsOpen(true);
  }, []);

  const close = useCallback(() => {
    if (isDeleting) {
      return;
    }
    setIsOpen(false);
    setTarget(null);
  }, [isDeleting]);

  useImperativeHandle(ref, () => ({ open, close }));

  const handleConfirm = useCallback(async () => {
    if (!target || isDeleting) {
      return;
    }
    setIsDeleting(true);
    try {
      await onConfirmDelete(target.reservationId, scope);
      close();
    } catch (error) {
      // El aviso de error lo muestra quien confirma; acá sólo se deja el modal
      // abierto para poder reintentar.
    } finally {
      setIsDeleting(false);
    }
  }, [target, isDeleting, scope, onConfirmDelete, close]);

  const days = target?.daysInSeries ?? 1;
  const isSeries = days > 1;

  return (
    <Modal isOpen={isOpen} size="md" toggle={close}>
      <Container fluid>
        <Row className="justify-content-center">
          <Col xs="auto">
            <Icon name="warning" size="60" />
          </Col>
        </Row>
        <p className="text-dark fs-22 mt-3 mb-4 text-center">
          {texts.delete.title}
        </p>
        <div className="text-dark fs-16 text-center">
          {isSeries ? texts.delete.seriesSubtitle(days) : texts.delete.subtitle}
        </div>

        {isSeries && (
          <Row className="justify-content-center mt-4">
            <Col xs="auto">
              {/*
                No hay componente de radio en global-ui —lo dice el propio
                RadioInput del DynamicForm—, así que se usa reactstrap, que es
                el precedente del repo.
              */}
              <FormGroup tag="fieldset" className="mb-0">
                {(['day', 'series'] as DeleteEventScope[]).map((option) => (
                  <FormGroup check key={option} className="mb-2">
                    <Label check className="text-dark fs-16">
                      <Input
                        type="radio"
                        name="deleteEventScope"
                        checked={scope === option}
                        disabled={isDeleting}
                        onChange={() => setScope(option)}
                      />{' '}
                      {option === 'day'
                        ? texts.delete.options.onlyThisDay
                        : texts.delete.options.allDays(days)}
                    </Label>
                  </FormGroup>
                ))}
              </FormGroup>
            </Col>
          </Row>
        )}

        <Row className="justify-content-center" style={{ marginTop: '40px' }}>
          <Button
            onClick={close}
            text={texts.delete.buttons.cancel}
            outlined
            disabled={isDeleting}
            style={{ width: '200px' }}
          />
          <Button
            onClick={handleConfirm}
            text={texts.delete.buttons.delete}
            loading={isDeleting}
            disabled={isDeleting}
            style={{ width: '200px', marginLeft: '20px' }}
          />
        </Row>
      </Container>
    </Modal>
  );
});

export default DeleteEventModal;
